// Target resolution for the manual IntelliSense refresh: the active query
// tab (Ctrl+Shift+R, Command Palette), one database picked in the Object
// Explorer, or every loaded database of a connection. Each resolver ends in
// `refreshIntelliSense` (refreshIntelliSense.ts); this module owns the
// "which pair, and should it run at all" decisions and the user feedback
// for the cases where it does not.

import type { DatabaseEngineId } from '@/core/engines';
import { toast } from '@/components/ui/toaster';

import {
  loadedIntelliSenseDatabases,
  supportsIntelliSenseRefresh,
} from '../../connections/services/language-services/reloadSchemaSnapshot';
import { getEngineForConnection, isConnectionActive } from '../../connections/utils/connectionLookup';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';

import {
  refreshIntelliSense,
  whenSchemaRefreshSettled,
  type IntelliSenseRefreshOutcome,
  type IntelliSenseRefreshSource,
} from './refreshIntelliSense';

export type RefreshForDatabaseOptions = {
  /**
   * Only refresh when IntelliSense holds a snapshot for the pair RIGHT NOW
   * (`loadedIntelliSenseDatabases` — engine-aware: on PostgreSQL / MySQL /
   * Oracle / SQLite that is the one database the connection's native cache
   * currently holds, a stale bus entry does not count). The explorer uses
   * this: a forced load of a database no tab uses would evict the snapshot
   * the open tabs read on those engines, and a database never loaded has
   * nothing stale to replace.
   */
  onlyIfLoaded?: boolean;
};

function engineOf(connectionId: string): DatabaseEngineId {
  return getEngineForConnection(connectionId) as DatabaseEngineId;
}

async function isLoaded(connectionId: string, database: string): Promise<boolean> {
  // Case-insensitive: SQL Server names can differ in case between the
  // explorer listing and the tab that loaded the snapshot.
  const wanted = database.toLowerCase();
  const loaded = await loadedIntelliSenseDatabases(connectionId, engineOf(connectionId));
  return loaded.some((db) => db.toLowerCase() === wanted);
}

/**
 * Refresh one database of a connection. Resolves the engine from the
 * connection store, skips connections that are not open (with a toast for
 * the surfaces that have no pill of their own to say so), and reports a
 * failed refetch with a toast.
 */
export async function refreshIntelliSenseForDatabase(
  connectionId: string,
  database: string,
  source: IntelliSenseRefreshSource,
  options: RefreshForDatabaseOptions = {},
): Promise<IntelliSenseRefreshOutcome | 'skipped'> {
  if (!connectionId || !database) return 'skipped';
  if (!isConnectionActive(connectionId)) {
    if (source !== 'explorer') {
      toast.default({
        title: 'IntelliSense not refreshed',
        description: 'The connection is not open — connect first, then refresh.',
      });
    }
    return 'skipped';
  }
  if (options.onlyIfLoaded && !(await isLoaded(connectionId, database))) return 'skipped';
  try {
    return await refreshIntelliSense({ connectionId, database, engine: engineOf(connectionId), source });
  } catch (error: unknown) {
    toast.error({
      title: 'IntelliSense refresh failed',
      description: error instanceof Error ? error.message : 'Could not reload the database schema.',
    });
    return 'skipped';
  }
}

/**
 * Refresh every database of `connectionId` that IntelliSense has loaded —
 * ONE AT A TIME, including a run some other surface already has in flight
 * for a pair (waited for, not skipped past). The per-connection native
 * caches (PostgreSQL, MySQL, Oracle, SQLite) hold one generation per
 * connection, so two forced loads in flight at once would cancel each
 * other's publish; one policy for every engine (MSSQL's per-database cache
 * would tolerate parallel loads, but the cross-engine rule says one policy).
 */
export async function refreshIntelliSenseForConnection(
  connectionId: string,
  source: IntelliSenseRefreshSource,
): Promise<void> {
  if (!isConnectionActive(connectionId)) return;
  for (const database of await loadedIntelliSenseDatabases(connectionId, engineOf(connectionId))) {
    const outcome = await refreshIntelliSenseForDatabase(connectionId, database, source);
    if (outcome === 'already-running') await whenSchemaRefreshSettled(connectionId, database);
  }
}

function activeQueryTabTarget(): { connectionId: string; database: string } | null {
  const { tabs, activeTabId } = useWorkspaceStore.getState();
  const tab = tabs.find((t) => t.id === activeTabId);
  if (!tab || tab.type !== 'query') return null;
  const connectionId = tab.connectionInfo?.connectionId;
  const database = tab.connectionInfo?.database;
  if (!connectionId || !database) return null;
  // An engine with nothing to reload (mock) is no target: the chord falls
  // through instead of flashing a spinner and counting an event for nothing.
  if (!supportsIntelliSenseRefresh(engineOf(connectionId))) return null;
  return { connectionId, database };
}

/** True when Ctrl+Shift+R / the palette entry has a target right now. */
export function hasActiveQueryTabRefreshTarget(): boolean {
  return activeQueryTabTarget() !== null;
}

/**
 * Refresh for the ACTIVE query tab's connection + database (Ctrl+Shift+R and
 * the Command Palette). Returns `false` when there is no such target — the
 * caller then lets the keystroke fall through.
 */
export function refreshIntelliSenseForActiveQueryTab(source: IntelliSenseRefreshSource): boolean {
  const target = activeQueryTabTarget();
  if (!target) return false;
  void refreshIntelliSenseForDatabase(target.connectionId, target.database, source);
  return true;
}
